'use client';

import { toast } from '@crabs-ai/ds-react';
import type {
  UploadFailureClassification,
  UploadFailureReason,
} from './upload-error-classifier';

type UploadToastTranslateFn = (
  key: string,
  values?: Record<string, string | number | Date>,
) => string;

/**
 * 동일 reason 으로 윈도우 내 추가 호출이 들어오면 합쳐서 단일 토스트로 표시한다.
 *
 * 정책:
 * - 윈도우 = 마지막 호출 후 800ms (debounce)
 * - 그룹 기준 = `reason` (다른 reason 은 독립 그룹)
 * - count === 1 → 기존 단건 카피 (`Error.client-upload-{reason}`) 그대로 사용
 * - count >= 2  → `Error.client-upload-aggregate` 로 N건 통합 카피
 *
 * 800ms 동안 토스트가 지연되지만 (단건 케이스 포함) 동시 다발 업로드 시
 * 동일 카피 N개가 큐에 쌓이는 노이즈를 제거한다.
 *
 * `MAX_CONCURRENT_UPLOADS = 5` 가 동시에 같은 사유로 실패해도 토스트는 1개.
 */
const AGGREGATE_WINDOW_MS = 800;

const AGGREGATE_DETAIL_KEY: Record<UploadFailureReason, string> = {
  NETWORK: 'Error.client-upload-aggregate-detail-network',
  TIMEOUT: 'Error.client-upload-aggregate-detail-timeout',
  FORBIDDEN: 'Error.client-upload-aggregate-detail-forbidden',
  UNSUPPORTED_FORMAT: 'Error.client-upload-aggregate-detail-unsupported-format',
  DECODE_FAILED: 'Error.client-upload-aggregate-detail-decode-failed',
  DURATION_UNRESOLVED:
    'Error.client-upload-aggregate-detail-duration-unresolved',
  AUDIO_OPEN_FAILED: 'Error.client-upload-aggregate-detail-audio-open-failed',
  NOT_VIDEO: 'Error.client-upload-aggregate-detail-not-video',
  NOT_AUDIO: 'Error.client-upload-aggregate-detail-not-audio',
  PLAN_LIMIT_EXCEEDED: 'Error.client-upload-aggregate-detail-plan-limit',
  PREFLIGHT_OFFLINE: 'Error.upload-preflight-aggregate-detail-offline',
  PREFLIGHT_TIMEOUT: 'Error.upload-preflight-aggregate-detail-unstable',
  PREFLIGHT_NETWORK_ERROR: 'Error.upload-preflight-aggregate-detail-unstable',
  PREFLIGHT_NETWORK_RESTRICTED:
    'Error.upload-preflight-aggregate-detail-network-restricted',
  PREFLIGHT_FORBIDDEN: 'Error.upload-preflight-aggregate-detail-service',
  PREFLIGHT_CORS_OR_ETAG: 'Error.upload-preflight-aggregate-detail-service',
  PREFLIGHT_UNKNOWN: 'Error.upload-preflight-aggregate-detail-unknown',
  UNKNOWN: 'Error.client-upload-aggregate-detail-unknown',
};

interface AggregateState {
  count: number;
  /** 첫 호출의 분류 결과를 보관해 단건일 때 그대로 카피로 사용. */
  first: UploadFailureClassification;
  timer: ReturnType<typeof setTimeout>;
}

const aggregates = new Map<UploadFailureReason, AggregateState>();

type ToastFn = typeof toast;
type FlushArgs = {
  state: AggregateState;
  reason: UploadFailureReason;
  t: UploadToastTranslateFn;
  toastFn: ToastFn;
};

const flushAggregate = ({ state, reason, t, toastFn }: FlushArgs): void => {
  if (state.count === 1) {
    toastFn({
      type: 'error',
      description: t(state.first.i18nKey, state.first.i18nValues),
      toastOptions: { position: 'top-center' },
    });
  } else {
    toastFn({
      type: 'error',
      description: t('Error.client-upload-aggregate', {
        count: state.count,
        detail: t(AGGREGATE_DETAIL_KEY[reason]),
      }),
      toastOptions: { position: 'top-center' },
    });
  }
  aggregates.delete(reason);
};

/**
 * 업로드 실패 토스트를 표시한다 (집계 정책 적용).
 *
 * 호출자는 root namespace 의 `useTranslations()` 를 t 로 전달해야 하며,
 * `Error.client-upload-*` 등 전체 경로 키를 resolve 할 수 있어야 한다.
 *
 * 두 번째 매개변수 `toastFn` 은 테스트에서만 주입한다 (기본값은 ds-react `toast`).
 */
export const showUploadFailureToast = (
  t: UploadToastTranslateFn,
  classification: UploadFailureClassification,
  toastFn: ToastFn = toast,
): void => {
  const { reason } = classification;
  const existing = aggregates.get(reason);

  if (existing) {
    existing.count += 1;
    clearTimeout(existing.timer);
    existing.timer = setTimeout(
      () => flushAggregate({ state: existing, reason, t, toastFn }),
      AGGREGATE_WINDOW_MS,
    );
    return;
  }

  const state: AggregateState = {
    count: 1,
    first: classification,
    timer: undefined as unknown as ReturnType<typeof setTimeout>,
  };
  state.timer = setTimeout(
    () => flushAggregate({ state, reason, t, toastFn }),
    AGGREGATE_WINDOW_MS,
  );
  aggregates.set(reason, state);
};

/**
 * 테스트 전용: 모듈 레벨 aggregate 상태 초기화.
 *
 * **호출 강제**: 모든 테스트는 시작 시점에 본 함수를 호출해야 한다 (test order
 * dependency 회피). 신규 테스트가 호출을 누락하면 같은 reason 으로 카운트가
 * leak 된다. 가능하면 `t.beforeEach` 또는 명시적 첫 줄 호출 패턴 사용.
 *
 * 프로덕션 호출 사이트에서 사용하지 말 것.
 */
export const __resetUploadFailureAggregatesForTest = (): void => {
  for (const state of aggregates.values()) {
    clearTimeout(state.timer);
  }
  aggregates.clear();
};
